/**
 * What counts as unread — pure.
 *
 * A per-bubble dot asks **is this message past my cursor right now**, and the answer has
 * to stay live: mark-read-on-view advances the cursor and the dot must go out with it.
 * The dots once read a *position* frozen for the visit instead, which made them immortal
 * — the cursor advanced, the frozen boundary did not move, and every dot stayed lit until
 * the thread unmounted.
 *
 * Three rules are encoded here rather than at the render sites, because all three are
 * decisions about the thread rather than about pixels:
 *
 * 1. **Own messages never count as unread.** The server's read cursor already skips
 *    them; a client that didn't would dot every message the viewer just sent.
 * 2. **Anything unresolved means nothing is unread yet.** The read cursor
 *    (`GET /:cid/unread`) and the viewer's identity (`auth/me`) are independent queries
 *    from the message page, so the thread routinely renders before either lands. Treating
 *    an absent cursor as "read nothing" lights the whole thread; treating absent roles as
 *    a side silently inverts rule 3 for a homeowner, dotting their OWN messages (the
 *    `viewerId` guard is unresolved in that same window). Either way the viewer watches
 *    dots they never earned go out, so `false` is the only safe answer until both land.
 * 2b. **When the server can answer per-SIDE, that answer wins.** See below.
 * 3. **A staff viewer only sees the OTHER side as unread** — see below. A homeowner's
 *    read state is personal (each household member has their own inbox), so for them
 *    every message past their own cursor counts, a co-owner's included.
 * 3b. **A bot message is never unread, for anyone.** The automated assistant posts as
 *    staff, so rule 3 already hides it from staff — but not from the homeowner, and the
 *    unread counters exclude it on BOTH sides (the staff audience never gets it, and
 *    the homeowner's personal count — `loadPersonalUnreadMessages` on the platform —
 *    filters it out). It is counted for nobody, so
 *    it is dotted for nobody. Which account is the bot is environment-specific, so this
 *    rides the wire as `authoredByBot` rather than being re-derived from a name or email.
 *
 * ## Why the audience set outranks the cursor (rule 2b)
 *
 * For STAFF, an order thread's unread state belongs to a SIDE, not to a person: `markMessagesRead`
 * clears `OrderNewMessage` for the whole audience whoever clicked, and that table is what
 * both the thread header's count and the order-list badge read. `ChannelMember
 * .lastReadMessageId` answers a different question — what did THIS person read — and
 * three separate bug reports were all the same contradiction between the two:
 *
 * 1. A teammate who never opened the thread kept a stale cursor and dotted messages the
 *    badge no longer counted.
 * 2. Marking read advanced only the clicker's cursor, so the rest of the team stayed lit.
 * 3. A staff user who is not a `ChannelMember` at all has NO cursor row, so it reads
 *    `null` — "has read nothing" — and the entire thread lit under a badge of 0. No
 *    write-side fix can reach that case: there is no row to advance.
 *
 * So when `audienceUnreadIds` is present the dots render from it and the cursor is not
 * consulted. Badge and dots become two renderings of one server answer, which is the only
 * shape in which they cannot drift, and it needs no membership row to be right. The
 * cursor remains the fallback for channel types with no audience (internal comments),
 * where read state genuinely is per person.
 *
 * ## Why the side of the author decides (rule 3)
 *
 * An order thread has exactly two audiences, and the platform tracks unread strictly
 * across them: `markMessageUnreadCore` writes `notifyTo = isOwner(author) ? MANAGER :
 * OWNER`, so a staff-authored message NEVER produces a staff-facing unread marker, and
 * the `OrderNewMessage` rows that feed both the order-list badge and the thread header's
 * count are one-sided by construction. A per-bubble dot that ignores the author's side
 * therefore contradicts the very count it sits under: the office thread lit 14 dots
 * against a badge of 10, the 4 extras being messages from a technician, a manager and
 * Birdie — people the viewer's own audience never gets notified about.
 *
 * The rule binds staff only. A homeowner is never handed the audience set
 * (`getAudienceUnread` answers `null` for them) and reads on their own cursor, which the
 * server counts as "every message past it that someone else wrote" — so another household
 * member's message is unread for them, and dotting it is what keeps the dots in step with
 * their inbox badge and the "N new messages" card on `/my/home`.
 *
 * Consequence worth knowing before reusing this: on a hypothetical single-audience
 * channel (everyone staff), every message is same-side and no dot would ever light for
 * staff — which is why the internal comment threads set `unreadDots: false` rather than
 * relying on this.
 *
 * "Which side" is the same test the platform makes (`MEMBER_ROLE_KEYS` in
 * `platform/src/lib/auth/member-role.ts`), read through the webapp's one home for it,
 * `getUserRole`. Roles ride along on every message as `userCreated.roles`, so no extra
 * query pays for this.
 */

import { getUserRole } from "@/libs/user-role";

/**
 * The viewer's read cursor.
 *
 * - `string` — the `Message.id` (a 24-char, time-sortable nanoid) of the newest message the viewer has read
 * - `null` — the cursor is empty; the viewer has read nothing in this channel
 * - `undefined` — `GET /:cid/unread` has not answered; nothing may be called unread yet
 */
export type ReadCursorId = string | null | undefined;

/**
 * What an unread decision needs off a message: who wrote it, and which side they are on.
 *
 * `userCreated` is nullish on the wire, so its absence is a real case — see
 * {@link isHomeownerSide}.
 */
export type UnreadMessage = {
  id: string;
  userCreatedId: string;
  userCreated?: { roles?: string[] | null } | null;
  /** Server-resolved; see rule 3b. Required so a caller cannot silently omit it. */
  authoredByBot: boolean;
};

export type UnreadContext = {
  lastReadMessageId: ReadCursorId;
  viewerId: string | undefined;
  /**
   * `Message.id`s the viewer's AUDIENCE still has unread — `audienceUnread.messageIds`
   * from `GET /:cid/unread`, the same rows its `count` counts.
   *
   * Present ⇒ authoritative, and the cursor is ignored (rule 2b). `undefined` ⇒ this
   * channel type has no audience, so fall back to the cursor. Note the distinction from
   * an empty array, which is a real answer meaning "the side has read everything".
   */
  audienceUnreadIds: readonly string[] | undefined;
  /**
   * The viewer's global roles (`auth/me`.roles) — which audience they read as.
   *
   * `undefined` means the query has not answered. It cannot mean "a user holding no
   * roles": the response types `roles` as a required array, so a loaded viewer is always
   * at worst `[]`. That is what makes it usable as a readiness signal (rule 2).
   */
  viewerRoles: readonly string[] | undefined;
};

/**
 * Whether a participant sits on the homeowner side of the thread rather than the staff
 * side.
 *
 * Only ever asked about a message's author, whose `userCreated` is nullish on the wire.
 * An author with no resolvable roles answers `false`, i.e. staff — which withholds the
 * dot from a staff viewer (the direction this rule exists to correct) but still shows it
 * to a homeowner. That asymmetry is deliberate rather than a fallback worth generalizing:
 * it only arises for a message whose `User` row is gone, and erring toward showing a
 * homeowner one extra dot beats hiding a real reply from them.
 *
 * The VIEWER's unresolved roles are NOT routed here — that is rule 2's guard, because
 * "not loaded yet" is not a side, and guessing one inverts every dot on the thread.
 */
function isHomeownerSide(roles: readonly string[] | null | undefined): boolean {
  return getUserRole(roles ? [...roles] : null).isOwner;
}

export function isMessageUnread<TMessage extends UnreadMessage>(
  message: TMessage,
  {
    lastReadMessageId,
    viewerId,
    viewerRoles,
    audienceUnreadIds,
  }: UnreadContext,
): boolean {
  if (viewerRoles === undefined) return false;
  // Kept ahead of the audience set: the set belongs to a whole side, so somebody else's
  // read can legitimately put the viewer's own message in it.
  if (message.userCreatedId === viewerId) return false;
  if (message.authoredByBot) return false;

  // Rule 2b — the side's own answer, when the server has one. The server files a message
  // under the opposite audience, so the cross-side rule below is already baked in here.
  if (audienceUnreadIds !== undefined) {
    return audienceUnreadIds.includes(message.id);
  }

  if (lastReadMessageId === undefined) return false;
  // Rule 3 — staff only: a homeowner's cursor is personal, so a co-owner's message counts.
  if (
    !isHomeownerSide(viewerRoles) &&
    !isHomeownerSide(message.userCreated?.roles)
  ) {
    return false;
  }
  // Ids are equal-length base36 nanoids with a timestamp prefix, so comparing them as
  // strings (JS compares code units) is creation order — the same order Postgres gives
  // them under COLLATE "C".
  return lastReadMessageId === null || message.id > lastReadMessageId;
}
